import axios from 'axios';
import { InvoiceDownloadResponse } from '../models/invoices/InvoiceDownloadApiResponse';
import { DownloadStatusResponse } from '../models/invoices/DownloadStatusResponse';
import { INVOICE_DOWNLOADER_BASE_URL } from './constants';

export const invoiceDownloaderClient = axios.create({
  baseURL: INVOICE_DOWNLOADER_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
});

export const sendStartLogin = async (
  username: string,
  password: string,
): Promise<string> => {
  try {
    const response = await invoiceDownloaderClient.post('/startLogin', {
      username,
      password,
    });
    console.debug('Start login response:', response?.data);
    if (response?.data.success === true) {
      return response?.data.data;
    } else {
      throw new Error(response?.data.error?.message);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getCaptchaImage = async (
  sessionId: string,
): Promise<InvoiceDownloadResponse> => {
  try {
    const response = await invoiceDownloaderClient.get(
      `/captchaImage?sessionId=${sessionId}`,
    );
    if (response?.data.success === true) {
      console.debug(
        'Captcha image response:',
        response?.data.data.slice(0, 100),
      );
    } else {
      console.debug('Captcha image response failed:', response?.data);
    }
    // In either case, return the response data with success status, image and error message if any
    return response?.data;
  } catch (error) {
    console.error('Error getting captcha image:', error);
    return {
      success: false,
      data: '',
      error: { statusCode: 500, message: `${error}` },
    };
  }
};

export const sendCaptchaSolution = async (
  sessionId: string,
  captchaCode: string,
): Promise<boolean> => {
  console.log('Sending captcha solution with payload:', {
    sessionId,
    captchaCode,
  });
  try {
    const response = await invoiceDownloaderClient.post('/captchaSolution', {
      sessionId: sessionId,
      solution: captchaCode,
    });
    console.log('Captcha solution response:', response);
    return response?.data.success === true;
  } catch (error) {
    console.error('Error sending captcha solution:', error);
    return false;
  }
};

export const getCheckAuthStatus = async (
  sessionId: string,
): Promise<InvoiceDownloadResponse> => {
  try {
    const response = await invoiceDownloaderClient.get(
      `/authStatus?sessionId=${sessionId}`,
    );
    return response?.data;
  } catch (error) {
    console.error('Error checking auth status:', error);
    return {
      success: false,
      data: '',
      error: {
        statusCode: 500,
        message: error instanceof Error ? error.message : String(error),
      },
    };
  }
};

export const downloadInvoices = async (
  sessionId: string,
  invoices: number,
  fromYear: string,
  fromMonth: string,
): Promise<InvoiceDownloadResponse> => {
  try {
    const response = await invoiceDownloaderClient.get(
      `/downloadInvoices?sessionId=${sessionId}&invoices=${invoices}&fromYear=${fromYear}&fromMonth=${fromMonth}`,
    );
    return response?.data;
  } catch (error) {
    console.error('Error downloading invoices:', error);
    return {
      success: false,
      data: '',
      error: { statusCode: 500, message: `${error}` },
    };
  }
};

export const cleanCaptchaSolutions =
  async (): Promise<InvoiceDownloadResponse> => {
    try {
      const response = await invoiceDownloaderClient.get('/cleanSolutions');
      return response?.data;
    } catch (error) {
      console.error('Error calling cleanSolutions:', error);
      throw error;
    }
  };

export const cleanupAllSessions =
  async (): Promise<InvoiceDownloadResponse> => {
    try {
      const response = await invoiceDownloaderClient.get('/cleanupAllSessions');
      return response?.data;
    } catch (error) {
      console.error('Error cleaning up all sessions:', error);
      return {
        success: false,
        data: '',
        error: { statusCode: 500, message: `${error}` },
      };
    }
  };

export const getCloseBrowser = async (
  sessionId: string,
): Promise<InvoiceDownloadResponse> => {
  try {
    const response = await invoiceDownloaderClient.get(
      `/closeBrowser?sessionId=${sessionId}`,
    );
    return response?.data;
  } catch (error) {
    console.error('Error closing browser:', error);
    return {
      success: false,
      data: '',
      error: { statusCode: 500, message: `${error}` },
    };
  }
};

export const getDownloadStatus = async (
  sessionId: string,
): Promise<DownloadStatusResponse> => {
  try {
    const response = await invoiceDownloaderClient.get(
      `/downloadStatus?sessionId=${sessionId}`,
    );
    return response?.data;
  } catch (error) {
    console.error('Error checking download status:', error);
    return {
      success: false,
      data: {
        totalInvoices: 0,
        completedInvoices: 0,
        totalDirect: 0,
        completedDirect: 0,
        totalTelephone: 0,
        completedTelephone: 0,
      },
      error: {
        statusCode: 500,
        message: error instanceof Error ? error.message : String(error),
      },
    };
  }
};
